import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    InputAdornment,
    Avatar,
    TextField,
} from '@mui/material';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
    useGetProfileQuery,
    useUpdateProfileMutation,
    useChangePasswordMutation,
} from '../services/authApi';
import { FormikTextField } from './FormikTextField';
import { FormikSubmitButton } from './FormikSubmitButton';
import { useState, useEffect } from 'react';
import { Visibility, VisibilityOff, CameraAlt } from '@mui/icons-material';
import profileImage from '../assets/profile.png';

const profileValidationSchema = yup.object({
    name: yup.string().required('Name is required'),
});

const passwordValidationSchema = yup.object({
    currentPassword: yup.string().required('Current Password is required'),
    newPassword: yup
        .string()
        .min(8, 'Password should be of minimum 8 characters length')
        .required('New Password is required'),
    confirmNewPassword: yup
        .string()
        .oneOf([yup.ref('newPassword'), null as any], 'Passwords must match')
        .required('Confirm New Password is required'),
});

interface ProfileDialogProps {
    open: boolean;
    onClose: () => void;
}

const ProfileDialog = ({ open, onClose }: ProfileDialogProps) => {
    const { data: profileArgs, refetch } = useGetProfileQuery(undefined, {
        skip: !open,
    });

    const user = useSelector((state: any) => state.user);

    const displayUser = {
        name: profileArgs?.name || user.name,
        email: profileArgs?.email || user.email,
        role: profileArgs?.role || user.role,
    };

    const [updateProfile] = useUpdateProfileMutation();
    const [changePassword] = useChangePasswordMutation();
    const { enqueueSnackbar } = useSnackbar();

    const [view, setView] = useState<'main' | 'edit' | 'password'>('main');

    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

    useEffect(() => {
        if (open) {
            refetch();
            setView('main');
            setShowCurrentPassword(false);
            setShowNewPassword(false);
            setShowConfirmNewPassword(false);
        }
    }, [open, refetch]);

    const handleBack = () => setView('main');

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" sx={{ '& .MuiDialog-paper': { borderRadius: '16px', p: 1 } }}>
            {view === 'main' && (
                <>
                    <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" fontWeight="bold">My Profile</Typography>
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                            <Box sx={{ position: 'relative' }}>
                                <Avatar
                                    src={profileImage}
                                    sx={{ width: 80, height: 80, mb: 2 }}
                                />
                                <IconButton
                                    sx={{
                                        position: 'absolute',
                                        bottom: 16,
                                        right: -4,
                                        backgroundColor: 'white',
                                        boxShadow: 1,
                                        padding: '4px',
                                        '&:hover': {
                                            backgroundColor: '#f5f5f5',
                                        }
                                    }}
                                    size="small"
                                >
                                    <CameraAlt fontSize="small" color="action" />
                                </IconButton>
                            </Box>

                            <Typography variant="body1" color="text.secondary" gutterBottom>
                                Display name
                            </Typography>
                            <Typography variant="h6" gutterBottom>
                                {displayUser.name}
                            </Typography>

                            <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
                                Username
                            </Typography>
                            <Typography variant="subtitle1" gutterBottom style={{ wordBreak: 'break-all' }}>
                                {displayUser.email}
                            </Typography>

                            <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
                                Role
                            </Typography>
                            <Typography variant="subtitle1" gutterBottom sx={{ textTransform: 'capitalize' }}>
                                {displayUser.role?.toLowerCase()}
                            </Typography>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'center', pb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={() => setView('edit')}
                            sx={{ borderRadius: '20px', mb: 1 }}
                        >
                            Edit Profile
                        </Button>
                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => setView('password')}
                            sx={{ borderRadius: '20px', marginLeft: '0 !important' }}

                        >
                            Change Password
                        </Button>
                        <Button onClick={onClose} sx={{ color: 'text.secondary', mt: 1 }}>Close</Button>
                    </DialogActions>
                </>
            )}

            {view === 'edit' && (
                <Formik
                    initialValues={{
                        name: displayUser.name || '',
                    }}
                    validationSchema={profileValidationSchema}
                    enableReinitialize
                    onSubmit={async (values, { setSubmitting }) => {
                        try {
                            await updateProfile(values).unwrap();
                            enqueueSnackbar('Profile updated successfully', { variant: 'success' });
                            refetch();
                            setView('main');
                        } catch (error: any) {
                            const errorMessage = error?.data?.message || 'Failed to update profile';
                            enqueueSnackbar(errorMessage, { variant: 'error' });
                        } finally {
                            setSubmitting(false);
                        }
                    }}
                >
                    <Form>
                        <DialogTitle>Edit Profile</DialogTitle>
                        <DialogContent>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                                <Avatar
                                    src={profileImage}
                                    sx={{ width: 80, height: 80, mb: 1 }}
                                />
                            </Box>
                            <FormikTextField
                                name="name"
                                label="Display Name"
                                fullWidth
                                margin="normal"
                            />
                            <TextField
                                label="Username"
                                value={displayUser.email}
                                disabled
                                fullWidth
                                margin="normal"
                                helperText="Username cannot be changed"
                            />
                        </DialogContent>
                        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                            <Button onClick={handleBack}>Cancel</Button>
                            <FormikSubmitButton loadingText="Saving...">Save</FormikSubmitButton>
                        </DialogActions>
                    </Form>
                </Formik>
            )}

            {view === 'password' && (
                <Formik
                    initialValues={{
                        currentPassword: '',
                        newPassword: '',
                        confirmNewPassword: '',
                    }}
                    validationSchema={passwordValidationSchema}
                    onSubmit={async (values, { setSubmitting }) => {
                        try {
                            await changePassword({
                                currentPassword: values.currentPassword,
                                newPassword: values.newPassword,
                            }).unwrap();
                            enqueueSnackbar('Password changed successfully', { variant: 'success' });
                            setView('main');
                        } catch (error: any) {
                            const errorMessage = error?.data?.message || 'Failed to change password';
                            enqueueSnackbar(errorMessage, { variant: 'error' });
                        } finally {
                            setSubmitting(false);
                        }
                    }}
                >
                    <Form>
                        <DialogTitle>Change Password</DialogTitle>
                        <DialogContent>
                            <FormikTextField
                                name="currentPassword"
                                label="Current Password"
                                type={showCurrentPassword ? 'text' : 'password'}
                                fullWidth
                                margin="normal"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                                edge="end"
                                            >
                                                {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <FormikTextField
                                name="newPassword"
                                label="New Password"
                                type={showNewPassword ? 'text' : 'password'}
                                fullWidth
                                margin="normal"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                                edge="end"
                                            >
                                                {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <FormikTextField
                                name="confirmNewPassword"
                                label="Confirm New Password"
                                type={showConfirmNewPassword ? 'text' : 'password'}
                                fullWidth
                                margin="normal"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                                                edge="end"
                                            >
                                                {showConfirmNewPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </DialogContent>
                        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                            <Button onClick={handleBack}>Cancel</Button>
                            <FormikSubmitButton loadingText="Changing...">Change Password</FormikSubmitButton>
                        </DialogActions>
                    </Form>
                </Formik>
            )}
        </Dialog>
    );
};

export default ProfileDialog;
